import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppLayout } from '../../components/layout';
import { Button, Input, Select, StatusBadge, CopyButton, EmptyState, Alert } from '../../components/core';
import { http } from '../../lib/api';
import { useUiStore } from '../../store/ui';
import type { DestinationDto, StreamPlatform } from '@livestreamx/shared-types';
import { NAV } from './nav';

const PLATFORMS: { platform: StreamPlatform; defaultUrl: string; label: string }[] = [
  { platform: 'youtube', defaultUrl: 'rtmp://a.rtmp.youtube.com/live2', label: 'YouTube Live' },
  { platform: 'facebook', defaultUrl: 'rtmp://live-api-s.facebook.com:443/rtmp', label: 'Facebook Live' },
  { platform: 'twitch', defaultUrl: 'rtmp://live.twitch.tv/app', label: 'Twitch' },
  { platform: 'linkedin', defaultUrl: 'rtmp://media.rtmp.linkedin.com/live', label: 'LinkedIn Live' },
  { platform: 'custom_rtmp', defaultUrl: '', label: 'Custom RTMP (manual URL)' },
];

export function CreateDestinationPage() {
  const { id: streamId, destinationId } = useParams<{ id: string; destinationId?: string }>();
  const ui = useUiStore();
  const [platform, setPlatform] = useState<string>('youtube');
  const [name, setName] = useState('');
  const [rtmpUrl, setRtmpUrl] = useState('');
  const [streamKey, setStreamKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [destination, setDestination] = useState<DestinationDto | null>(null);
  const selected = PLATFORMS.find((p) => p.platform === platform);
  useEffect(() => {
    if (!destinationId) return;
    const fetchDestination = async () => {
      try {
        const res = await http.get<DestinationDto>(`/api/tenant/streams/${streamId}/destinations/${destinationId}`);
        setDestination(res);
        // Populate form
        setPlatform(res.platform);
        setName(res.name);
        setRtmpUrl(res.rtmpUrl);
        // Note: streamKey is not returned in the DestinationDto for security reasons.
        // We leave it empty and require the user to re-enter it if they want to change it.
      } catch (err) {
        setError('Failed to load destination');
        ui.toast('danger', 'Failed to load destination');
      }
    };
    fetchDestination();
  }, [destinationId, streamId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (destinationId) {
        // Edit mode: PATCH
        const updateData: any = {
          platform: platform as StreamPlatform,
          name,
          rtmpUrl: rtmpUrl || selected?.defaultUrl,
        };
        if (streamKey) {
          updateData.streamKey = streamKey;
        }
        await http.patch(`/api/tenant/streams/${streamId}/destinations/${destinationId}`, updateData);
        ui.toast('success', 'Destination updated');
      } else {
        // Create mode
        await http.post(`/api/tenant/streams/${streamId}/destinations`, {
          platform: platform as StreamPlatform,
          name,
          rtmpUrl: rtmpUrl || selected?.defaultUrl,
          streamKey,
        });
        ui.toast('success', 'Destination added');
      }
      window.location.href = `/streams/${streamId}/destinations`;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save destination');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="max-w-xl">
        <div className="flex items-center justify-between mb-2">
          <h1 className="page-title">{destinationId ? 'Edit destination' : 'Add destination'}</h1>
          <Link to={`/streams/${streamId}/destinations`}><Button variant="secondary">Back</Button></Link>
        </div>
        <p className="text-muted mb-4">Send this stream to YouTube, Facebook, Twitch, or any custom RTMP endpoint.</p>

        {error ? <Alert kind="danger">{error}</Alert> : null}

        <form onSubmit={submit} className="destination-form">
          <Select
            label="Platform"
            value={platform}
            options={PLATFORMS.map((p) => ({ value: p.platform, label: p.label }))}
            onChange={(v) => { setPlatform(v); if (v !== 'custom_rtmp') setRtmpUrl(PLATFORMS.find((p) => p.platform === v)?.defaultUrl ?? ''); }}
          />
          <Input label="Destination name" value={name} onChange={setName} placeholder="e.g. YouTube channel" required autoComplete="off" />
          <Input label="RTMP URL" value={rtmpUrl} onChange={setRtmpUrl} placeholder="rtmp://…" hint={platform === 'custom_rtmp' ? 'Enter the RTMP ingest URL for your destination.' : 'Auto-filled from the selected platform.'} />
          <Input label="Stream key / password" type="password" value={streamKey} onChange={setStreamKey} placeholder="Enter the stream key from the platform" required autoComplete="off" hint="This is encrypted at rest. Never share this key." />
          <div className="flex justify-end gap-2 mt-4">
            <Link to={`/streams/${streamId}/destinations`}><Button variant="secondary" disabled={busy}>Cancel</Button></Link>
            <Button type="submit" variant="primary" disabled={busy || !name.trim() || !streamKey.trim()}>
              {busy ? (destinationId ? 'Updating…' : 'Adding…') : (destinationId ? 'Update destination' : 'Add destination')}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
